import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { SUPABASE_ENABLED } from "@/lib/supabase";
import { getSession, onAuthChange, signIn, signUp, joinDemoProject } from "@/lib/auth";
import { LOGO_DATA_URI } from "@/assets/logo";

// Public demo login, shown on the sign-in screen for easy access.
const DEMO = { email: "demo@terratrace.eu", password: "GridProofDemo2026" };

/**
 * Gates the app behind Supabase Auth. Once signed in, the user joins the
 * Ballyroe demo project (so authenticated RLS grants data access), then the
 * children (the engine app, which still picks an in-app role) render.
 *
 * When Supabase is not configured / offline-only, the gate is transparent.
 */
export default function AuthGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (!SUPABASE_ENABLED) {
      setAuthed(true);
      setReady(true);
      return;
    }
    let active = true;
    getSession().then((s) => {
      if (!active) return;
      setAuthed(!!s);
      setReady(true);
    });
    const off = onAuthChange((s) => {
      setAuthed(!!s);
    });
    return () => {
      active = false;
      off();
    };
  }, []);

  if (!ready) return null;
  if (!authed) return <SignInScreen />;
  return <>{children}</>;
}

function SignInScreen() {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === "signup") {
        await signUp(email.trim(), password, fullName.trim() || email.trim());
        // autoconfirm is on, so a session is issued immediately; ensure sign-in
        await signIn(email.trim(), password);
      } else {
        await signIn(email.trim(), password);
      }
      await joinDemoProject("qcmanager");
      // onAuthChange in AuthGate flips us into the app.
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  // One-click demo sign-in (credentials shown on the page).
  async function useDemo() {
    setEmail(DEMO.email);
    setPassword(DEMO.password);
    setMode("signin");
    setErr(null);
    setBusy(true);
    try {
      await signIn(DEMO.email, DEMO.password);
      await joinDemoProject("qcmanager");
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Demo sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-overlay" style={{ position: "fixed", inset: 0 }}>
      <div className="login-box" style={{ maxWidth: 420 }}>
        <div className="login-logo">
          <div className="login-glyph">
            <img src={LOGO_DATA_URI} alt="TerraTrace logo" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </div>
          <div>
            <div className="login-brand serif">GridProof</div>
            <div className="login-sub">powered by TerraTrace</div>
          </div>
        </div>
        <div className="login-project" style={{ marginTop: 16 }}>
          {mode === "signin" ? "Sign in to continue" : "Create your account"}
        </div>

        <form onSubmit={submit} style={{ marginTop: 18, display: "grid", gap: 12 }}>
          {mode === "signup" && (
            <Field label="Full name">
              <input
                className="gp-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                placeholder="Catalin Radu"
              />
            </Field>
          )}
          <Field label="Email">
            <input
              className="gp-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@terratrace.eu"
            />
          </Field>
          <Field label="Password">
            <input
              className="gp-input"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              placeholder="••••••••"
            />
          </Field>

          {err && (
            <div style={{ color: "var(--rust)", fontSize: 13, lineHeight: 1.4 }}>{err}</div>
          )}

          <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 4 }}>
            {busy ? "…" : mode === "signin" ? "Sign in →" : "Create account →"}
          </button>
        </form>

        <div style={{ marginTop: 16, fontSize: 13, color: "var(--dim)" }}>
          {mode === "signin" ? "No account yet? " : "Already have an account? "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setErr(null);
              setMode(mode === "signin" ? "signup" : "signin");
            }}
            style={{ color: "var(--steel)", fontWeight: 600 }}
          >
            {mode === "signin" ? "Create one" : "Sign in"}
          </a>
        </div>

        <div className="demo-box">
          <div className="demo-h">Demo account</div>
          <div className="demo-cred mono">{DEMO.email}</div>
          <div className="demo-cred mono">{DEMO.password}</div>
          <button type="button" className="btn btn-primary demo-btn" disabled={busy} onClick={useDemo}>
            {busy ? "…" : "Use demo account →"}
          </button>
        </div>
      </div>

      <style>{`
        .gp-input{
          width:100%; box-sizing:border-box; padding:10px 12px;
          background:var(--panel2,#141d27); color:var(--ink);
          border:1px solid var(--line); border-radius:8px;
          font:14px/1.2 'IBM Plex Sans',system-ui,sans-serif;
        }
        .gp-input:focus{ outline:none; border-color:var(--steel); }
        .demo-box{
          margin-top:18px; padding:12px 14px; border:1px dashed var(--line);
          border-radius:8px; background:var(--panel2,#141d27);
        }
        .demo-h{
          font-size:10px; letter-spacing:.08em; text-transform:uppercase;
          color:var(--dim); margin-bottom:6px;
        }
        .demo-cred{ font-size:13px; color:var(--ink); line-height:1.5; }
        .demo-btn{ width:100%; margin-top:10px; }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, color: "var(--dim)", textTransform: "uppercase", letterSpacing: ".04em" }}>
        {label}
      </span>
      {children}
    </label>
  );
}
